
import React, { useState } from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileMinusIcon, CheckIcon, XIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

interface BenposBeneficiariesProps {
  data: any[];
  tdsWaivers?: Record<string, boolean>;
  onTdsWaiver?: (folioId: string, hasWaiver: boolean) => void;
}

export const BenposBeneficiaries: React.FC<BenposBeneficiariesProps> = ({ 
  data,
  tdsWaivers = {},
  onTdsWaiver = () => {}
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [tdsWaiverDialogOpen, setTdsWaiverDialogOpen] = useState(false);
  const [selectedFolio, setSelectedFolio] = useState<string | null>(null);
  const [fileSelected, setFileSelected] = useState(false);
  
  // Filter data based on search query and category filter
  const filteredData = data.filter((item) => {
    // Search filter
    const matchesSearch = 
      item.folio.toLowerCase().includes(searchQuery.toLowerCase()) || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Category filter
    const matchesCategory = categoryFilter === "all" || 
      (categoryFilter === "retail" && item.category === "Retail") ||
      (categoryFilter === "institutional" && item.category === "Institutional") ||
      (categoryFilter === "nonResident" && item.category === "Non-Resident");
    
    return matchesSearch && matchesCategory;
  });

  // Open TDS waiver dialog
  const openTdsWaiverDialog = (folioId: string) => {
    setSelectedFolio(folioId);
    setTdsWaiverDialogOpen(true);
  };

  // Handle TDS waiver confirmation
  const confirmTdsWaiver = () => {
    if (selectedFolio && fileSelected) {
      onTdsWaiver(selectedFolio, true);
      setTdsWaiverDialogOpen(false);
      setFileSelected(false);
      toast.success("TDS waiver document uploaded successfully");
    } else {
      toast.error("Please select a document to upload");
    }
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFileSelected(true);
    } else {
      setFileSelected(false);
    }
  };

  // Remove TDS waiver
  const removeTdsWaiver = (folioId: string) => {
    onTdsWaiver(folioId, false);
    toast.info("TDS waiver removed");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <div className="w-full md:w-1/2">
          <Input
            placeholder="Search by name or folio number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="retail">Retail</SelectItem>
              <SelectItem value="institutional">Institutional</SelectItem>
              <SelectItem value="nonResident">Non-Resident</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">Export</Button>
        </div>
      </div>
      
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Folio Number</TableHead>
              <TableHead>Beneficiary Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Units Held</TableHead>
              <TableHead className="text-right">Face Value</TableHead>
              <TableHead>Bank Account</TableHead>
              <TableHead>IFSC Code</TableHead>
              <TableHead>TDS Waiver</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredData.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium">{item.folio}</TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.category}</TableCell>
                <TableCell className="text-right">{item.units.toLocaleString()}</TableCell>
                <TableCell className="text-right">₹{item.faceValue.toLocaleString()}</TableCell>
                <TableCell>{item.bankAccount}</TableCell>
                <TableCell>{item.ifscCode}</TableCell>
                <TableCell>
                  {tdsWaivers[item.folio] ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                        <CheckIcon className="h-3 w-3 mr-1" /> Waiver Applied
                      </Badge>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => removeTdsWaiver(item.folio)}
                        className="h-6 w-6 p-0"
                      >
                        <XIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => openTdsWaiverDialog(item.folio)}
                    >
                      <FileMinusIcon className="h-4 w-4 mr-2" />
                      Attach Form 15G/H
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center">
                  No beneficiaries found matching your filters
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      
      <div className="flex justify-between text-sm text-muted-foreground">
        <div>
          Showing {filteredData.length} of {data.length} beneficiaries
        </div>
        <div>
          Total Units: {data.reduce((sum, item) => sum + item.units, 0).toLocaleString()}
        </div>
      </div>
      
      {/* TDS Waiver Dialog */}
      <Dialog open={tdsWaiverDialogOpen} onOpenChange={setTdsWaiverDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload TDS Waiver Document</DialogTitle>
            <DialogDescription>
              Upload Form 15G/H to apply TDS waiver for this beneficiary
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Document Type</label>
              <Select defaultValue="15g">
                <SelectTrigger>
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15g">Form 15G (For individuals below 60 years)</SelectItem>
                  <SelectItem value="15h">Form 15H (For senior citizens)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Upload Document</label>
              <Input 
                type="file" 
                accept=".pdf,.jpg,.jpeg,.png" 
                onChange={handleFileChange}
              />
              <p className="text-xs text-muted-foreground">
                Accepted formats: PDF, JPG, PNG (Max size: 5MB)
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTdsWaiverDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmTdsWaiver}>
              Upload & Apply Waiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
